import {
  type ConsoleAccountId,
  defaultTenantSettings,
  TenantIdSchema,
  type TenantSettings,
  TenantSettingsSchema,
} from '@plato/shared';
import type { ConsoleAuth } from './console-auth.ts';
import type { ConsoleData, TenantSummary } from './console-data.ts';
import { demoConversations } from './demo-conversations.ts';
import { demoEvalsData } from './demo-evals-data.ts';
import { demoSkillsData } from './demo-skills-data.ts';
import { demoUsageData } from './demo-usage.ts';
import type { ApiKeyRow, IntegrationSetup, McpServerRow, TeamView, ToolRow } from './rows.ts';

const EXAMPLE_CO: TenantSummary = {
  id: TenantIdSchema.parse('tnt_demo1'),
  name: 'Example Software Co',
  role: 'admin',
  via: 'member',
};
const DEMO_TENANTS: readonly TenantSummary[] = [
  EXAMPLE_CO,
  { id: TenantIdSchema.parse('tnt_demo2'), name: 'Sample Apps Inc', role: 'viewer', via: 'member' },
];

const DEMO_SETTINGS: TenantSettings = {
  ...defaultTenantSettings(),
  assistantName: 'Sample Helper',
  models: {
    choices: [
      { id: 'example-model-fast', label: 'Fast' },
      { id: 'example-model-deep', label: 'Deep' },
    ],
    default: 'example-model-fast',
    showModelIds: false,
  },
};

const demo = {
  settings: DEMO_SETTINGS,
  integrations: {
    apiOrigin: 'https://api.plato.example',
    embedOrigin: 'https://embed.plato.example',
    publishableKey: 'plato_pk_test_exampleexampleexample',
    allowedOrigins: ['https://app.example.com', 'http://localhost:4000'],
    mcpServers: [
      {
        name: 'orders',
        title: null,
        url: 'https://mcp.example.com/orders',
        enabled: true,
        credential: 'oauth_refresh',
      },
      {
        name: 'inventory',
        title: null,
        url: 'https://mcp.example.com/inventory',
        enabled: false,
        credential: 'session_token',
      },
      {
        name: 'scheduling',
        title: 'Shift scheduling',
        url: 'https://mcp.scheduling.example/mcp',
        enabled: true,
        credential: 'user_sign_in',
      },
      {
        name: 'payments',
        title: 'Payments',
        url: 'https://mcp.payments.example/mcp',
        enabled: true,
        credential: 'user_api_key',
      },
    ],
  } as IntegrationSetup,
  keys: [
    {
      id: 'key_demo1',
      kind: 'secret',
      mode: 'test',
      createdAt: '2026-10-01T09:00:00.000Z',
      revokedAt: null,
      publishableKey: null,
    },
    {
      id: 'key_demo2',
      kind: 'publishable',
      mode: 'test',
      createdAt: '2026-10-01T09:00:00.000Z',
      revokedAt: null,
      publishableKey: 'plato_pk_test_exampleexampleexample',
    },
  ] as ApiKeyRow[],
  tools: [
    {
      source: 'orders',
      kind: 'mcp',
      sourceEnabled: true,
      name: 'search_orders',
      title: 'Search orders',
      sourceDescription: 'Search orders by customer, date or status.',
      description: 'Search orders by customer, date or status.',
      descriptionReplaced: false,
      enabled: true,
      lastSeenAt: '2026-10-15T12:00:00.000Z',
    },
    {
      source: 'orders',
      kind: 'mcp',
      sourceEnabled: true,
      name: 'cancel_order',
      title: 'Cancel order',
      sourceDescription: 'Cancel an order that has not shipped.',
      description: 'Cancel an order that has not shipped.',
      descriptionReplaced: false,
      enabled: false,
      lastSeenAt: '2026-10-15T12:00:00.000Z',
    },
    {
      source: 'inventory',
      kind: 'mcp',
      sourceEnabled: false,
      name: 'stock_levels',
      title: null,
      sourceDescription: 'Current stock per warehouse.',
      description: 'Current stock per warehouse.',
      descriptionReplaced: false,
      enabled: true,
      lastSeenAt: '2026-10-12T08:30:00.000Z',
    },
  ] as ToolRow[],
  team: {
    members: [
      {
        id: 'cacc_demo1',
        name: 'Sam Example',
        email: 'sam@example.com',
        role: 'admin',
        joinedAt: '2026-10-01T09:00:00.000Z',
      },
      {
        id: 'cacc_demo2',
        name: 'Alex Sample',
        email: 'alex@example.com',
        role: 'viewer',
        joinedAt: '2026-10-08T15:30:00.000Z',
      },
    ],
    invites: [],
  } as TeamView,
};

const always =
  <T>(value: T) =>
  async (): Promise<T> =>
    value;
const done = async (): Promise<void> => undefined;

export const demoConsoleData: ConsoleData = {
  ...demoEvalsData,
  ...demoSkillsData,
  demo: true,
  ...demoConversations,

  ...demoUsageData,

  getAssistantSettings: async () => demo.settings,
  updateAssistantSettings: async (_tenant, changes) => {
    const next: Record<string, unknown> = { ...demo.settings };
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) delete next[key];
      else next[key] = value;
    }
    demo.settings = TenantSettingsSchema.parse(next);
    return demo.settings;
  },

  listTools: async () => demo.tools,
  updateTool: async (_tenant, source, name, change) => {
    demo.tools = demo.tools.map((tool) => {
      if (tool.source !== source || tool.name !== name) return tool;
      const description =
        change.description === undefined
          ? tool.description
          : (change.description ?? tool.sourceDescription);
      return {
        ...tool,
        enabled: change.enabled ?? tool.enabled,
        description,
        descriptionReplaced: description !== tool.sourceDescription,
      };
    });
  },
  resetTool: async (_tenant, source, name) => {
    demo.tools = demo.tools.map((tool) =>
      tool.source === source && tool.name === name
        ? {
            ...tool,
            enabled: true,
            description: tool.sourceDescription,
            descriptionReplaced: false,
          }
        : tool,
    );
  },

  getIntegrationSetup: async () => demo.integrations,
  listApiKeys: async () => demo.keys,
  setAllowedOrigins: async (_tenant, origins) => {
    demo.integrations = { ...demo.integrations, allowedOrigins: [...origins] };
  },
  updateMcpServer: async (_tenant, name, change) => {
    demo.integrations = {
      ...demo.integrations,
      mcpServers: demo.integrations.mcpServers.map((server) =>
        server.name === name ? { ...server, ...change } : server,
      ),
    };
  },
  createMcpServer: async (_tenant, server) => {
    if (demo.integrations.mcpServers.some((existing) => existing.name === server.name)) {
      throw new Error(`A server named ${server.name} already exists.`);
    }
    const row: McpServerRow = {
      name: server.name,
      title: server.title,
      url: server.url,
      enabled: true,
      credential: server.connectWith === 'sign_in' ? 'user_sign_in' : 'user_api_key',
    };
    demo.integrations = {
      ...demo.integrations,
      mcpServers: [...demo.integrations.mcpServers, row],
    };
    return row;
  },
  deleteMcpServer: async (_tenant, name) => {
    demo.integrations = {
      ...demo.integrations,
      mcpServers: demo.integrations.mcpServers.filter((server) => server.name !== name),
    };
  },
  createApiKey: async (_tenant, kind) => {
    const keyId = `key_demo${demo.keys.length + 1}`;
    const value = `plato_${kind === 'secret' ? 'sk' : 'pk'}_test_demo${demo.keys.length + 1}`;
    demo.keys = [
      ...demo.keys,
      {
        id: keyId,
        kind,
        mode: 'test',
        createdAt: new Date().toISOString(),
        revokedAt: null,
        publishableKey: kind === 'publishable' ? value : null,
      },
    ];
    return { keyId, value };
  },
  revokeApiKey: async (_tenant, keyId) => {
    demo.keys = demo.keys.map((key) =>
      key.id === keyId ? { ...key, revokedAt: new Date().toISOString() } : key,
    );
  },

  getTeam: async () => demo.team,
  inviteMember: async (_tenant, email, role) => {
    const inviteId = `cinv_demo${demo.team.invites.length + 1}`;
    demo.team = {
      ...demo.team,
      invites: [
        ...demo.team.invites,
        {
          id: inviteId,
          email,
          role,
          expiresAt: new Date(Date.now() + 7 * 86_400_000).toISOString(),
        },
      ],
    };
    return { inviteId, link: 'https://console.plato.example/#/invite/demo-token' };
  },
  revokeInvite: async (_tenant, inviteId) => {
    demo.team = {
      ...demo.team,
      invites: demo.team.invites.filter((invite) => invite.id !== inviteId),
    };
  },
  setMemberRole: async (_tenant, accountId, role) => {
    demo.team = {
      ...demo.team,
      members: demo.team.members.map((member) =>
        member.id === accountId ? { ...member, role } : member,
      ),
    };
  },
  removeMember: async (_tenant, accountId) => {
    demo.team = {
      ...demo.team,
      members: demo.team.members.filter((member) => member.id !== accountId),
    };
  },

  listAuditEvents: always([
    {
      id: 'l1',
      at: '2026-10-16T09:12:00.000Z',
      actor: 'Sam Example',
      event: 'mcp_server.enable',
      details: { server: 'orders' },
    },
    {
      id: 'l2',
      at: '2026-10-15T17:40:00.000Z',
      actor: 'Sam Example',
      event: 'tenant.settings',
      details: { changed: ['assistantName'] },
    },
    {
      id: 'l3',
      at: '2026-10-15T11:03:00.000Z',
      actor: 'Plato operator (ops)',
      event: 'api_key.mint',
      details: { keyId: 'key_demo1', kind: 'secret' },
    },
  ]),
};

export const demoConsoleAuth: ConsoleAuth = {
  getMe: async () => ({
    account: {
      id: 'cacc_demo1' as ConsoleAccountId,
      email: 'sam@example.com',
      name: 'Sam Example',
      plato_staff: false,
    },
    tenants: DEMO_TENANTS.map((tenant) => ({ ...tenant, key_mode: 'test' as const })),
  }),
  listSignInProviders: always([]),
  signInUrl: () => '#/',
  devSignIn: done,
  signOut: done,
  acceptInvite: async () => ({ ...EXAMPLE_CO, key_mode: 'test' }),
};
